import {
  useAttachFileDocumentToTaskState,
  useAttachTaskStateDocument,
  useCreateDocumentMutation,
  useCreateDocumentVersionMutation,
  useCreateTaskState,
  useCreateUserUploadDocument,
  useDeleteAdHocTask,
  useDeleteDocumentMutation,
  useDocEngine,
  useGetDevice,
  useGetDevices,
  useGetDocuments,
  useGetDocumentsTemplates,
  useGetDocumentsWithAllVersions,
  useGetOrgTasks,
  useGetTasks,
  useGetTaskStateActivity,
  useGetUser,
  useGetUsersByOrg,
  useUpdateTaskState,
} from "@hooks";
import { Document, TaskManualStatus } from "@models";
import ArrowOutwardOutlinedIcon from "@mui/icons-material/ArrowOutwardOutlined";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionIcon from "@mui/icons-material/Description";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SchoolIcon from "@mui/icons-material/School";
import type { SelectChangeEvent } from "@mui/material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  ListSubheader,
  Menu,
  MenuItem,
  Select,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import dayjs, { Dayjs } from "dayjs";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Navigate,
  Link as RouterLink,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { TrainingDocumentModal } from "src/components/Trainings/TrainingDocumentModal";
import { UserAvatar } from "src/components/UserAvatar";
import { UnifiedTask } from "src/services";
import { ConfirmationModal, PageBreadcrumb, PageHeader } from "../components";
import { AssigneeButton } from "../components/Tasks/AssigneeButton";
import { DeadlinePicker } from "../components/Tasks/DeadlinePicker";
import { EditTaskModal } from "../components/Tasks/EditTaskModal";
import { MarkdownText } from "../components/ui/MarkdownText";
import { ROUTE_SLUGS, ROUTES } from "../config/navigation/routes";
import {
  DocStatus,
  DocUIStatus,
  DocumentStatus,
  DocumentUIStatus,
} from "../types";
import {
  docStatusDictionary,
  docUIDictionary,
  getDocUIStatus,
  manualTaskStatusDictionary,
} from "../utils/documents";
import { fillRouteWithSlugs } from "../utils/navigation";
import {
  getDocumentTaskStatus,
  getTaskStatusInfo,
  isTaskManualStatus,
  normalizeTaskManualStatus,
} from "../utils/tasks";

function TaskDetail() {
  const { orgId = "", taskId = "" } = useParams();
  // Fetch all tasks to find the current one
  const {
    data: tasks,
    isLoading,
    isFetching,
  } = useGetOrgTasks({
    orgId,
    scope: "all",
    enabled: !!orgId,
    placeholderTaskId: taskId,
  });

  // Look up by task ID first, then fall back to taskKey (for tasks without a state yet)
  const task = tasks?.find((t) => t.id === taskId || t.taskKey === taskId);

  if (isLoading || (!task && isFetching)) return <TaskDetailSkeleton />;
  if (task?.taskType === "QUALITY_EVENT" && task.actionPath) {
    return <Navigate to={task.actionPath} replace />;
  }
  return <TaskDetailContent task={task} />;
}

const TaskDetailContent = ({ task }: { task: UnifiedTask | undefined }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { orgId = "", deviceId: urlDeviceId } = useParams<{
    orgId: string;
    deviceId: string;
  }>();
  const { data: user } = useGetUser();
  const { data: orgUsers = [] } = useGetUsersByOrg({ orgId });
  const { data: devices = [] } = useGetDevices({ orgId });

  const searchParams = new URLSearchParams(location.search);
  const queryDeviceId = searchParams.get("deviceId") ?? "";
  const returnToParam = searchParams.get("returnTo");
  const returnToState = (location.state as { returnTo?: "device" | "org" })
    ?.returnTo;
  const returnDeviceState = (location.state as { deviceId?: string })?.deviceId;
  const returnTo =
    returnToState ||
    (returnToParam === "device" || returnToParam === "org"
      ? returnToParam
      : undefined) ||
    (queryDeviceId ? "device" : undefined);
  const returnDeviceId = returnDeviceState || queryDeviceId;

  // Determine deviceId: use URL param, task's device, query context, or first device as fallback
  const deviceId =
    urlDeviceId || task?.device?.id || queryDeviceId || devices[0]?.id || "";
  const documentDeviceId = task?.device?.id || deviceId;
  const { data: documentDevice } = useGetDevice({
    orgId,
    deviceId: documentDeviceId,
  });
  const docEngine = useDocEngine(documentDeviceId);

  const { data: templatesConfig } = useGetDocumentsTemplates({
    orgId,
    deviceId: documentDeviceId,
  });
  const { data: documents = [] } = useGetDocuments(orgId, documentDeviceId);
  const { data: roadmapTasks = [] } = useGetTasks(orgId, documentDeviceId);
  const { data: taskActivity = [] } = useGetTaskStateActivity({
    orgId,
    deviceId,
    taskStateId: task?.id ?? "",
    enabled: Boolean(task?.id),
  });

  // State
  const [status, setStatus] = useState<TaskManualStatus>(TaskManualStatus.TODO);
  const [assigneeId, setAssigneeId] = useState<string | null>(null);
  const [dueDate, setDueDate] = useState<Dayjs | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [trainingModalOpen, setTrainingModalOpen] = useState(false);
  const [moreOptionsAnchorEl, setMoreOptionsAnchorEl] =
    useState<null | HTMLElement>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<Document | null>(
    null
  );
  const [deleteDocumentConfirmOpen, setDeleteDocumentConfirmOpen] =
    useState(false);
  const [createDocumentModalOpen, setCreateDocumentModalOpen] = useState(false);
  const [linkedDocumentTitle, setLinkedDocumentTitle] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Mutations
  const createTaskState = useCreateTaskState({ orgId, deviceId });
  const attachTaskStateDocument = useAttachTaskStateDocument({
    orgId,
    deviceId,
  });
  const createDocumentMutation = useCreateDocumentMutation();
  const createDocumentVersionMutation = useCreateDocumentVersionMutation(
    orgId,
    documentDeviceId
  );
  const { uploadFileAsDocument } = useCreateUserUploadDocument(
    orgId,
    task?.device?.id || undefined
  );
  const updateTaskState = useUpdateTaskState({ orgId, deviceId });
  const attachFileDocumentToTaskState = useAttachFileDocumentToTaskState({
    orgId,
    deviceId,
  });
  const deleteAdHocTask = useDeleteAdHocTask({ orgId });
  const deleteDocumentMutation = useDeleteDocumentMutation();

  const attachmentDocumentIds = useMemo(() => {
    if (!task) return [];

    return Array.from(
      new Set([
        ...(task.documentId ? [task.documentId] : []),
        ...(task.fileDocuments ?? []).map((document) => document.id),
      ])
    );
  }, [task]);

  const { data: attachmentDocumentsById = {}, failedDocIds = [] } =
    useGetDocumentsWithAllVersions(orgId, attachmentDocumentIds);

  // Update local state when task changes
  useEffect(() => {
    if (!task) return;

    const nextStatus = normalizeTaskManualStatus(task.status);
    setStatus(nextStatus);
    setAssigneeId(task.assignee?.id || null);
    setDueDate(task.dueDate ? dayjs(task.dueDate) : null);
  }, [task]);

  // Helper to ensure task state exists before updating
  const ensureTaskStateAndUpdate = useCallback(
    async (updateFn: (stateId: string) => Promise<void>) => {
      if (!task) return;

      // If task has an id (meaning state exists), update directly
      if (task.id) {
        await updateFn(task.id);
      } else if (task.taskKey) {
        // Create state first, then update
        const newState = await createTaskState.mutateAsync({
          taskKey: task.taskKey,
        });
        await updateFn(newState.id);
      }
    },
    [task, createTaskState]
  );

  const handleStatusChange = async (e: SelectChangeEvent) => {
    const { value } = e.target;
    if (!isTaskManualStatus(value)) return;
    setStatus(value);

    await ensureTaskStateAndUpdate(async (stateId) => {
      await updateTaskState.mutateAsync({
        stateId,
        data: { manualStatus: value },
      });
    });
  };

  const handleAssigneeChange = async (newAssigneeId: string | null) => {
    setAssigneeId(newAssigneeId);

    await ensureTaskStateAndUpdate(async (stateId) => {
      await updateTaskState.mutateAsync({
        stateId,
        data: { assigneeId: newAssigneeId },
      });
    });
  };

  const selectedAssignee = useMemo(() => {
    if (!assigneeId) return null;
    return (
      orgUsers.find((orgUser) => orgUser.id === assigneeId) ??
      task?.assignee ??
      null
    );
  }, [assigneeId, orgUsers, task?.assignee]);

  const handleDueDateChange = async (newDate: Dayjs | null) => {
    if (newDate && !newDate.isValid()) {
      return;
    }
    setDueDate(newDate);

    await ensureTaskStateAndUpdate(async (stateId) => {
      await updateTaskState.mutateAsync({
        stateId,
        data: { dueDate: newDate?.toISOString() || null },
      });
    });
  };

  const handleOpenCreateDocumentModal = () => {
    if (!task) return;
    const defaultTitle = `${task.title} Document`.trim();
    setLinkedDocumentTitle(defaultTitle || "Task Document");
    setCreateDocumentModalOpen(true);
  };

  const ensureTaskStateId = useCallback(async () => {
    if (!task) return null;

    if (task.id) {
      return task.id;
    }

    if (task.taskKey) {
      const newState = await createTaskState.mutateAsync({
        taskKey: task.taskKey,
      });
      return newState.id;
    }

    return null;
  }, [task, createTaskState]);

  const navigateToDocument = useCallback(
    (docId: string, versionId?: string) => {
      if (!documentDeviceId) return;

      const documentUrl = fillRouteWithSlugs(ROUTES.QMS_OPEN_DOC, {
        [ROUTE_SLUGS.ORG_ID]: orgId,
        [ROUTE_SLUGS.DEVICE_ID]: documentDeviceId,
        [ROUTE_SLUGS.DOC_ID]: docId,
      });

      navigate(versionId ? `${documentUrl}?v=${versionId}` : documentUrl);
    },
    [documentDeviceId, navigate, orgId]
  );

  const handleCreateLinkedDocument = async () => {
    if (!task) return;

    const title = linkedDocumentTitle.trim();
    if (!title) return;

    const stateId = await ensureTaskStateId();
    if (!stateId) return;

    const document = await createDocumentMutation.mutateAsync({
      orgId,
      body: {
        ...(task.device?.id ? { deviceId: task.device.id } : {}),
        title,
      },
    });

    const documentVersion = await createDocumentVersionMutation.mutateAsync({
      docId: document.id,
    });

    await attachTaskStateDocument.mutateAsync({
      stateId,
      documentId: document.id,
    });

    setCreateDocumentModalOpen(false);
    setLinkedDocumentTitle("");
    navigateToDocument(document.id, documentVersion.id);
  };

  const handleOpenLinkedDocument = () => {
    if (!task?.documentId || !documentDeviceId) {
      return;
    }

    const linkedDocumentUrl = fillRouteWithSlugs(ROUTES.QMS_OPEN_DOC, {
      [ROUTE_SLUGS.ORG_ID]: orgId,
      [ROUTE_SLUGS.DEVICE_ID]: documentDeviceId,
      [ROUTE_SLUGS.DOC_ID]: task.documentId,
    });
    navigate(linkedDocumentUrl);
  };

  // File upload handler
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const stateId = await ensureTaskStateId();
      if (!stateId) return;

      const fileDocument = await uploadFileAsDocument(file);

      await attachFileDocumentToTaskState.mutateAsync({
        stateId,
        fileDocumentId: fileDocument.id,
      });
      navigateToDocument(fileDocument.id);
    } finally {
      setIsUploading(false);
      // Clear input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleOpenDeleteDocument = (document: Document) => {
    setDocumentToDelete(document);
    setDeleteDocumentConfirmOpen(true);
  };

  const handleDeleteDocumentConfirm = async () => {
    if (!documentToDelete) return;

    await deleteDocumentMutation.mutateAsync({
      orgId,
      deviceId: documentDeviceId,
      docId: documentToDelete.id,
    });
    setDeleteDocumentConfirmOpen(false);
    setDocumentToDelete(null);
  };

  const getFileDocumentUrl = (fileDocumentId: string) =>
    fillRouteWithSlugs(ROUTES.QMS_OPEN_DOC, {
      [ROUTE_SLUGS.ORG_ID]: orgId,
      [ROUTE_SLUGS.DEVICE_ID]: documentDeviceId,
      [ROUTE_SLUGS.DOC_ID]: fileDocumentId,
    });

  const handleOpenFileDocument = (fileDocumentId: string) => {
    if (!documentDeviceId) return;
    navigate(getFileDocumentUrl(fileDocumentId));
  };

  // More options menu handlers
  const handleMoreOptionsClick = (event: React.MouseEvent<HTMLElement>) => {
    setMoreOptionsAnchorEl(event.currentTarget);
  };

  const handleMoreOptionsClose = () => {
    setMoreOptionsAnchorEl(null);
  };

  // Delete task handlers
  const handleDeleteClick = () => {
    handleMoreOptionsClose();
    setDeleteConfirmOpen(true);
  };

  const handleEditClick = () => {
    handleMoreOptionsClose();
    setEditModalOpen(true);
  };

  const handleDeleteConfirm = async (
    deleteAttachedDocuments: boolean = false
  ) => {
    if (!task?.taskKey) return;

    const taskKey = task.taskKey;

    if (deleteAttachedDocuments) {
      for (const document of deletableAttachmentDocuments) {
        await deleteDocumentMutation.mutateAsync({
          orgId,
          deviceId: documentDeviceId,
          docId: document.id,
        });
      }
    }

    await deleteAdHocTask.mutateAsync({ taskKey });
    setDeleteConfirmOpen(false);
    navigate(tasksUrl);
  };

  const tasksBaseUrl = fillRouteWithSlugs(ROUTES.TASKS, {
    [ROUTE_SLUGS.ORG_ID]: orgId,
  });
  const tasksUrl =
    returnTo === "device" && returnDeviceId
      ? `${tasksBaseUrl}?deviceId=${returnDeviceId}`
      : returnTo === "org"
        ? tasksBaseUrl
        : urlDeviceId
          ? `${tasksBaseUrl}?deviceId=${urlDeviceId}`
          : tasksBaseUrl;

  const isManualTask = task?.taskType === "MANUAL";
  const isDocumentTask = task?.taskType === "DOCUMENT";
  const isTrainingTask = task?.taskType === "TRAINING";
  const taskTemplateKey = task?.templateKey ?? null;
  const taskDescription = task?.description ?? "";
  const normalizedDescription = taskDescription
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/\u00a0/g, " ")
    .trim();
  const hasDescription = normalizedDescription.length > 0;

  const docUIStatus = useMemo(() => {
    if (!isDocumentTask || !taskTemplateKey) return undefined;
    const roadmapTask = roadmapTasks.find(
      (roadmapTask) => roadmapTask.taskKey === taskTemplateKey
    );
    return roadmapTask ? getDocUIStatus(roadmapTask) : undefined;
  }, [isDocumentTask, roadmapTasks, taskTemplateKey]);

  const taskDocumentId = useMemo(() => {
    if (task?.documentId) return task.documentId;
    if (!taskTemplateKey) return null;
    const matchingDocs = documents.filter(
      (doc) => doc.templateKey === taskTemplateKey
    );
    if (matchingDocs.length === 0) return null;
    return matchingDocs
      .slice()
      .sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      )[0].id;
  }, [documents, task?.documentId, taskTemplateKey]);

  const docStatusValue = useMemo<DocStatus | DocUIStatus>(() => {
    if (!isDocumentTask || !taskTemplateKey || !user) {
      return DocumentStatus.AVAILABLE;
    }
    if (docUIStatus) {
      return docUIStatus;
    }
    return getDocumentTaskStatus({
      templateKey: taskTemplateKey,
      documents,
      user,
      orgId,
    });
  }, [documents, docUIStatus, isDocumentTask, orgId, taskTemplateKey, user]);

  const taskStatusInfo = useMemo(() => {
    if (isManualTask || isTrainingTask) {
      // TRAINING tasks reuse the MANUAL status palette since manualStatus is
      // the source of truth (driven by mark-as-read / sign actions).
      return getTaskStatusInfo({ taskType: "MANUAL", status });
    }
    if (isDocumentTask) {
      return getTaskStatusInfo({
        taskType: "DOCUMENT",
        status: docStatusValue,
      });
    }
    return null;
  }, [docStatusValue, isDocumentTask, isManualTask, isTrainingTask, status]);

  const statusSelectProps = {
    inputProps: {
      "data-testid": "task-status-select",
      "aria-label": "Status",
    },
  };

  const renderStatusOption = (statusInfo: {
    label: string;
    icon: JSX.Element;
  }) => (
    <Box
      className="flex items-center gap-1"
      data-testid="status-option"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        lineHeight: 1,
        whiteSpace: "nowrap",
        "& svg": {
          display: "block",
        },
      }}
    >
      <span
        className="flex items-center leading-none"
        data-testid="status-option-icon"
      >
        {statusInfo.icon}
      </span>
      <span className="leading-none">{statusInfo.label}</span>
    </Box>
  );

  const manualStatusOptions = Object.entries(manualTaskStatusDictionary).map(
    ([statusKey, statusInfo]) => (
      <MenuItem key={statusKey} value={statusKey}>
        {renderStatusOption(statusInfo)}
      </MenuItem>
    )
  );

  const documentStatusOptions = [
    ...(docUIStatus === DocumentUIStatus.LOCKED
      ? [
          <MenuItem
            key={DocumentUIStatus.LOCKED}
            value={DocumentUIStatus.LOCKED}
          >
            {renderStatusOption(docUIDictionary[DocumentUIStatus.LOCKED])}
          </MenuItem>,
        ]
      : []),
    ...Object.entries(docStatusDictionary).map(([statusKey, statusInfo]) => (
      <MenuItem key={statusKey} value={statusKey}>
        {renderStatusOption(statusInfo)}
      </MenuItem>
    )),
  ];

  const compactSelectSx = (theme: Theme) => ({
    minWidth: 0,
    width: "auto",
    display: "inline-flex",
    "& .MuiSelect-select": {
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      lineHeight: "20px",
      py: 0.5,
      px: 1,
      ml: -1,
      borderRadius: 1,
      width: "auto",
      minWidth: 0,
      fontSize: theme.typography.pxToRem(13),
      fontWeight: theme.typography.button.fontWeight,
      letterSpacing: theme.typography.button.letterSpacing,
      "&:hover": {
        backgroundColor: "action.hover",
      },
    },
  });

  const statusMenuProps = {
    PaperProps: {
      sx: {
        minWidth: "max-content",
      },
    },
    MenuListProps: {
      sx: {
        whiteSpace: "nowrap",
      },
    },
  };

  const compactDateSx = {
    width: "auto",
    minWidth: 0,
    "& .MuiInput-underline:before": {
      borderBottom: "none",
    },
    "& .MuiInput-underline:after": {
      borderBottom: "none",
    },
    "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
      borderBottom: "none",
    },
    "& .MuiInputBase-root": {
      width: "auto",
      minWidth: 0,
      px: 1,
      py: 0.5,
      borderRadius: 1,
      display: "inline-flex",
      "&:hover": {
        backgroundColor: "action.hover",
      },
    },
    "& .MuiInputBase-input": {
      lineHeight: "20px",
      p: 0,
      width: "auto",
      minWidth: 0,
    },
    "& .MuiInputAdornment-root": {
      ml: 0.5,
      opacity: 0,
      pointerEvents: "none",
      transition: "opacity 150ms ease",
    },
    "& .MuiInputBase-root:hover .MuiInputAdornment-root, & .MuiInputBase-root.Mui-focused .MuiInputAdornment-root":
      {
        opacity: 1,
        pointerEvents: "auto",
      },
  };

  const hasTemplateConfig = Boolean(
    templatesConfig && taskTemplateKey && templatesConfig[taskTemplateKey]
  );
  const canOpenDocument = Boolean(
    isDocumentTask && documentDeviceId && taskTemplateKey && hasTemplateConfig
  );
  const dueDateFormat = "MM/DD/YYYY";
  const isDueDateOverdue = dueDate ? dueDate.isBefore(dayjs(), "day") : false;
  const dueDatePlaceholder = "No due date";
  const dueDateInputSize =
    (dueDate
      ? dueDate.format(dueDateFormat).length
      : dueDatePlaceholder.length) + 1;
  const handleOpenDocument = () => {
    if (
      !canOpenDocument ||
      !taskTemplateKey ||
      !documentDevice ||
      !user ||
      !templatesConfig
    )
      return;
    void docEngine.navigateToWizardOrQMS({
      templateKey: taskTemplateKey,
      documentId: taskDocumentId ?? undefined,
    });
  };

  const getUserDisplayName = (userId: string | null | undefined) => {
    if (!userId) return null;
    const userMatch = orgUsers.find((orgUser) => orgUser.id === userId);
    if (!userMatch) return null;
    const fullName =
      `${userMatch.firstName ?? ""} ${userMatch.lastName ?? ""}`.trim();
    return fullName || userMatch.email;
  };

  const getActorMeta = (entry: {
    actorType?: string | null;
    actorUserId?: string | null;
    actorIntegrationId?: string | null;
    onBehalfOfUserId?: string | null;
  }) => {
    const actorUserName = getUserDisplayName(entry.actorUserId ?? null);
    if (actorUserName) {
      return {
        label: actorUserName,
        initials: undefined,
        userId: entry.actorUserId ?? undefined,
      };
    }

    if (entry.actorType === "integration") {
      const label = "Integration";
      return {
        label,
        initials: "INT",
        userId: undefined,
      };
    }

    if (entry.actorType === "system") {
      const label = "System";
      return { label, initials: "SYS", userId: undefined };
    }

    const fallback = "Unknown";
    return { label: fallback, initials: "?", userId: undefined };
  };

  const activityItems = useMemo(() => {
    if (!taskActivity || taskActivity.length === 0) return [];
    const items: {
      id: string;
      message: string;
      occurredAt: Date;
      actor: ReturnType<typeof getActorMeta>;
    }[] = [];
    const statusLabel = (value: string | null | undefined) => {
      if (!value) return "Unknown";
      const entry =
        manualTaskStatusDictionary[
          value as keyof typeof manualTaskStatusDictionary
        ];
      return entry?.label ?? value;
    };

    const formatDueDate = (value: string | null | undefined) => {
      if (!value) return "No due date";
      return dayjs(value).format(dueDateFormat);
    };

    taskActivity.forEach((entry) => {
      const actor = getActorMeta(entry);
      const occurredAt =
        entry.occurredAt instanceof Date
          ? entry.occurredAt
          : new Date(entry.occurredAt as unknown as string);

      if (entry.action === "INSERT") {
        items.push({
          id: `${entry.id}-insert`,
          message: `${actor.label} created the task`,
          occurredAt,
          actor,
        });
        return;
      }

      if (entry.action === "DELETE") {
        items.push({
          id: `${entry.id}-delete`,
          message: `${actor.label} deleted the task`,
          occurredAt,
          actor,
        });
        return;
      }

      if (entry.action !== "UPDATE") return;

      const changes = entry.changes as Record<string, { old: any; new: any }>;
      if (!changes) return;

      const manualStatusChange = changes.manualStatus;
      if (manualStatusChange) {
        const oldLabel = statusLabel(manualStatusChange.old);
        const newLabel = statusLabel(manualStatusChange.new);
        items.push({
          id: `${entry.id}-manualStatus`,
          message: `${actor.label} moved status from ${oldLabel} to ${newLabel}`,
          occurredAt,
          actor,
        });
      }

      const assigneeChange = changes.assigneeId;
      if (assigneeChange) {
        const oldName = getUserDisplayName(assigneeChange.old) ?? "Unassigned";
        const newName = getUserDisplayName(assigneeChange.new) ?? "Unassigned";
        let message = "";
        if (!assigneeChange.old && assigneeChange.new) {
          message = `${actor.label} assigned to ${newName}`;
        } else if (assigneeChange.old && !assigneeChange.new) {
          message = `${actor.label} cleared assignee`;
        } else if (assigneeChange.old && assigneeChange.new) {
          message = `${actor.label} reassigned from ${oldName} to ${newName}`;
        }
        if (message) {
          items.push({
            id: `${entry.id}-assigneeId`,
            message,
            occurredAt,
            actor,
          });
        }
      }

      const dueDateChange = changes.dueDate;
      if (dueDateChange) {
        let message = "";
        if (!dueDateChange.old && dueDateChange.new) {
          message = `${actor.label} set due date to ${formatDueDate(dueDateChange.new)}`;
        } else if (dueDateChange.old && !dueDateChange.new) {
          message = `${actor.label} cleared due date`;
        } else if (dueDateChange.old && dueDateChange.new) {
          message = `${actor.label} changed due date to ${formatDueDate(dueDateChange.new)}`;
        }
        if (message) {
          items.push({
            id: `${entry.id}-dueDate`,
            message,
            occurredAt,
            actor,
          });
        }
      }

      const notesChange = changes.notesDocumentId;
      if (notesChange && !notesChange.old && notesChange.new) {
        items.push({
          id: `${entry.id}-notesDocumentId`,
          message: `${actor.label} linked a document`,
          occurredAt,
          actor,
        });
      }
    });

    return items.sort(
      (a, b) => a.occurredAt.getTime() - b.occurredAt.getTime()
    );
  }, [taskActivity, orgUsers]);

  if (!user) {
    return <TaskDetailSkeleton />;
  }

  if (!task) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center">
        <Typography color="error">Task not found</Typography>
        <Button href={tasksUrl}>Go to Tasks</Button>
      </div>
    );
  }

  const isAssigneeDisabled =
    task.taskType === "APPROVAL" || task.taskType === "TRAINING";
  // Check if task is an ad-hoc task (can be deleted)
  const isAdHocTask = task.taskKey?.startsWith("adhoc-") ?? false;
  // Check if current user is the author
  const isAuthor = user?.id === task.createdBy;
  const isAdmin = !!user?.permissions?.includes("write:configuration");
  // Can only click delete if user is admin and author of an ad-hoc task
  const canDelete = isAdHocTask && isAdmin && isAuthor;
  const canEdit = isAdHocTask && isAdmin;
  // Tooltip explains why the button is disabled
  const deleteTooltip = !isAdHocTask
    ? "Managed tasks cannot be deleted"
    : !canDelete
      ? "Only available to admin authors"
      : "";
  const editTooltip = !isAdHocTask
    ? "Managed tasks cannot be edited"
    : !isAdmin
      ? "Only admins can edit tasks"
      : "";

  // Get file documents from the task data
  const fileDocuments = task.fileDocuments ?? [];
  const linkedDocument = task.documentId
    ? attachmentDocumentsById[task.documentId]
    : undefined;
  const linkedDocumentName = linkedDocument?.title || "Untitled document";
  const hasLinkedDocument = Boolean(task.documentId);
  const canOpenLinkedDocument = Boolean(task.documentId && documentDeviceId);
  const canCreateLinkedDocument = Boolean(task.id || task.taskKey);
  const isCreatingLinkedDocument =
    attachTaskStateDocument.isPending ||
    createDocumentMutation.isPending ||
    createDocumentVersionMutation.isPending ||
    createTaskState.isPending;

  const hasReleasedRevision = (document?: Document | null) =>
    Boolean(document?.versions.some((version) => version.revision !== null));

  const canDeleteTaskDocument = (documentId?: string | null) => {
    if (!documentId) return false;
    if (failedDocIds.includes(documentId)) return false;
    return !hasReleasedRevision(attachmentDocumentsById[documentId]);
  };

  const deletableAttachmentDocuments = attachmentDocumentIds
    .map((documentId) => attachmentDocumentsById[documentId])
    .filter((document): document is Document => Boolean(document))
    .filter((document) => !hasReleasedRevision(document));

  return (
    <div className="flex flex-1 h-full flex-col p-0 gap-y-4">
      <PageHeader
        pageTitleComponent={
          <PageBreadcrumb
            items={[{ label: "Tasks", to: tasksUrl }, { label: task.title }]}
          />
        }
        rightPanelToggleRequiresDevice={false}
        rightButtons={
          <>
            <IconButton
              onClick={handleMoreOptionsClick}
              size="small"
              aria-label="more options"
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={moreOptionsAnchorEl}
              open={Boolean(moreOptionsAnchorEl)}
              onClose={handleMoreOptionsClose}
              disablePortal
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <Tooltip title={editTooltip} placement="left">
                <span>
                  <MenuItem onClick={handleEditClick} disabled={!canEdit}>
                    Edit task
                  </MenuItem>
                </span>
              </Tooltip>

              <ListSubheader sx={{ color: "error.main" }}>
                Danger Zone
              </ListSubheader>

              <Tooltip title={deleteTooltip} placement="left">
                <span>
                  <MenuItem
                    onClick={handleDeleteClick}
                    disabled={!canDelete || deleteAdHocTask.isPending}
                  >
                    Delete task
                  </MenuItem>
                </span>
              </Tooltip>
            </Menu>
            <EditTaskModal
              open={editModalOpen}
              onClose={() => setEditModalOpen(false)}
              orgId={orgId}
              task={{
                taskKey: task.taskKey ?? "",
                title: task.title,
                description: task.description,
                parentCardKey: task.parentCardKey ?? null,
                device: task.device,
              }}
            />
          </>
        }
      />

      <Box className="flex flex-col max-w-[800px] mx-auto w-full py-2 px-3">
        <Box className="flex flex-col gap-2">
          <Typography component="h1" variant="h4" className="font-semibold">
            {task.title}
          </Typography>
          {hasDescription ? (
            <Box
              className="py-2"
              sx={{
                "& .markdown-content": {
                  border: "none",
                  padding: 0,
                },
                "& .markdown-content .wmde-markdown": {
                  backgroundColor: "transparent",
                  margin: 0,
                  padding: 0,
                },
                "& .markdown-content .wmde-markdown > :first-of-type": {
                  marginTop: 0,
                },
                "& .markdown-content .wmde-markdown > :last-of-type": {
                  marginBottom: 0,
                },
              }}
            >
              <MarkdownText>{taskDescription}</MarkdownText>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No description
            </Typography>
          )}
        </Box>

        <Divider className="my-4" sx={{ borderColor: "divider" }} />

        <Box
          className="flex flex-wrap items-center gap-3"
          data-testid="task-details-row"
        >
          {(isManualTask || isDocumentTask || isTrainingTask) && (
            // Shift left to offset the select's internal padding.
            <Tooltip
              title={isTrainingTask ? "Complete task to mark complete" : ""}
              placement="top"
            >
              <FormControl variant="standard" sx={compactSelectSx}>
                <Select
                  size="small"
                  value={
                    isManualTask || isTrainingTask ? status : docStatusValue
                  }
                  onChange={isManualTask ? handleStatusChange : undefined}
                  disabled={!isManualTask}
                  inputProps={statusSelectProps.inputProps}
                  disableUnderline
                  MenuProps={statusMenuProps}
                  renderValue={() =>
                    taskStatusInfo ? renderStatusOption(taskStatusInfo) : ""
                  }
                >
                  {isManualTask || isTrainingTask
                    ? manualStatusOptions
                    : documentStatusOptions}
                </Select>
              </FormControl>
            </Tooltip>
          )}

          <AssigneeButton
            assignee={selectedAssignee}
            onAssigneeChange={handleAssigneeChange}
            showName
            disabled={isAssigneeDisabled}
          />

          <Tooltip
            title="Due Date"
            disableHoverListener={!isTrainingTask}
            placement="top"
          >
            <Box component="span">
              <DeadlinePicker
                value={dueDate}
                onChange={(newDate) => {
                  void handleDueDateChange(newDate);
                }}
                onClear={() => {
                  void handleDueDateChange(null);
                }}
                ariaLabel="Due date"
                format={dueDateFormat}
                placeholder={dueDatePlaceholder}
                clearAriaLabel="Clear due date"
                disabled={isTrainingTask}
                textFieldProps={{
                  variant: "standard",
                  hiddenLabel: true,
                  inputProps: {
                    size: Math.max(dueDateInputSize, 1),
                  },
                  InputProps: {
                    disableUnderline: true,
                  },
                  sx: {
                    ...compactDateSx,
                    ...(isDueDateOverdue && {
                      "& .MuiInputBase-input": {
                        ...compactDateSx["& .MuiInputBase-input"],
                        color: "error.main",
                      },
                    }),
                  },
                }}
              />
            </Box>
          </Tooltip>

          {isTrainingTask && task.sourceProgramName && (
            <Tooltip
              title={
                task.sourceProgramId
                  ? "View training program"
                  : "Part of training program"
              }
              placement="top"
            >
              <Chip
                icon={<SchoolIcon fontSize="small" />}
                label={task.sourceProgramName}
                size="small"
                variant="outlined"
                sx={{ maxWidth: 240 }}
                {...(task.sourceProgramId
                  ? {
                      component: RouterLink,
                      to: fillRouteWithSlugs(ROUTES.TRAINING_DETAIL, {
                        [ROUTE_SLUGS.ORG_ID]: orgId,
                        [ROUTE_SLUGS.PROGRAM_ID]: task.sourceProgramId,
                      }),
                      clickable: true,
                    }
                  : {})}
              />
            </Tooltip>
          )}

          {task.device && (
            <Chip label={task.device.name} size="small" variant="outlined" />
          )}
        </Box>

        {isDocumentTask && (
          <Box>
            <Button
              variant="text"
              size="small"
              color="inherit"
              startIcon={<ArrowOutwardOutlinedIcon />}
              onClick={handleOpenDocument}
              disabled={!canOpenDocument}
            >
              Go to document
            </Button>
          </Box>
        )}

        {isTrainingTask &&
          (() => {
            const isAssignedToCurrentUser =
              task.assignee?.id != null && task.assignee.id === user?.id;
            return (
              <Box className="mt-2 flex flex-col gap-3">
                {!isAssignedToCurrentUser && (
                  <Alert severity="info">
                    Only{" "}
                    {task.assignee
                      ? `${task.assignee.firstName ?? task.assignee.email}`
                      : "the assignee"}{" "}
                    can complete this training.
                  </Alert>
                )}

                <Box className="flex flex-wrap gap-2 items-center">
                  {task.trainingSignedAt ? (
                    <Chip
                      color="success"
                      label={`Signed ${dayjs(task.trainingSignedAt).format(
                        "MMM D, YYYY h:mm A"
                      )}`}
                    />
                  ) : (
                    <Button
                      variant="contained"
                      onClick={() => setTrainingModalOpen(true)}
                      disabled={!isAssignedToCurrentUser || !task.id}
                      sx={{ my: 1 }}
                    >
                      {!task.trainingReadAt
                        ? "Complete training"
                        : task.trainingQuizId && !task.trainingQuizPassedAt
                          ? "Take quiz"
                          : "Sign training"}
                    </Button>
                  )}
                </Box>
              </Box>
            );
          })()}

        {isManualTask && (
          <>
            <Box
              className="mt-4 flex flex-col gap-2"
              data-testid="notes-attachments-section"
            >
              <Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {hasLinkedDocument ? (
                    <Box
                      data-testid="linked-document-row"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        p: 1,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        cursor: canOpenLinkedDocument ? "pointer" : "default",
                        "&:hover": canOpenLinkedDocument
                          ? {
                              borderColor: "primary.light",
                              backgroundColor: "action.hover",
                            }
                          : {},
                      }}
                      onClick={
                        canOpenLinkedDocument
                          ? handleOpenLinkedDocument
                          : undefined
                      }
                    >
                      <DescriptionIcon color="action" fontSize="small" />
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {linkedDocumentName}
                      </Typography>
                      {linkedDocument &&
                        canDeleteTaskDocument(linkedDocument.id) && (
                          <Tooltip title="Delete in eQMS">
                            <span>
                              <IconButton
                                size="small"
                                aria-label="delete linked document"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleOpenDeleteDocument(linkedDocument);
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      <Button
                        variant="text"
                        size="small"
                        color="inherit"
                        startIcon={<ArrowOutwardOutlinedIcon />}
                        disabled={!canOpenLinkedDocument}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleOpenLinkedDocument();
                        }}
                      >
                        Go to document
                      </Button>
                    </Box>
                  ) : (
                    <Button
                      variant="text"
                      size="small"
                      color="inherit"
                      startIcon={<DescriptionIcon />}
                      sx={{ alignSelf: "flex-start" }}
                      onClick={handleOpenCreateDocumentModal}
                      disabled={
                        !canCreateLinkedDocument || isCreatingLinkedDocument
                      }
                    >
                      Create document
                    </Button>
                  )}

                  {failedDocIds.length > 0 && (
                    <Alert
                      severity="warning"
                      data-testid="attachment-load-warning"
                    >
                      Some attachment details could not be loaded. Delete
                      actions are hidden until that metadata is available.
                    </Alert>
                  )}

                  {fileDocuments.map((fileDoc) => (
                    <Box
                      key={fileDoc.id}
                      data-testid={`file-document-row-${fileDoc.id}`}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        p: 1,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        cursor: documentDeviceId ? "pointer" : "default",
                        "&:hover": documentDeviceId
                          ? {
                              borderColor: "primary.light",
                              backgroundColor: "action.hover",
                            }
                          : {},
                      }}
                      onClick={
                        documentDeviceId
                          ? () => handleOpenFileDocument(fileDoc.id)
                          : undefined
                      }
                    >
                      <DescriptionIcon color="action" fontSize="small" />
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {fileDoc.title || fileDoc.filename || "Untitled"}
                      </Typography>
                      <Box
                        sx={{
                          ml: "auto",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        {canDeleteTaskDocument(fileDoc.id) && (
                          <Tooltip title="Delete in eQMS">
                            <span>
                              <IconButton
                                size="small"
                                aria-label={`delete attachment ${fileDoc.id}`}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  const fullDocument =
                                    attachmentDocumentsById[fileDoc.id];
                                  if (fullDocument) {
                                    handleOpenDeleteDocument(fullDocument);
                                  }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        <Button
                          variant="text"
                          size="small"
                          color="inherit"
                          startIcon={<ArrowOutwardOutlinedIcon />}
                          disabled={!documentDeviceId}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleOpenFileDocument(fileDoc.id);
                          }}
                        >
                          Go to document
                        </Button>
                      </Box>
                    </Box>
                  ))}
                  <Button
                    component="label"
                    variant="text"
                    size="small"
                    color="inherit"
                    startIcon={
                      isUploading ? (
                        <CircularProgress size={16} />
                      ) : (
                        <AttachFileIcon />
                      )
                    }
                    sx={{ alignSelf: "flex-start" }}
                    disabled={isUploading || !canCreateLinkedDocument}
                  >
                    Upload File
                    <input
                      ref={fileInputRef}
                      type="file"
                      hidden
                      onChange={handleFileUpload}
                    />
                  </Button>
                </Box>
              </Box>
            </Box>

            <Divider className="my-4" sx={{ borderColor: "divider" }} />
          </>
        )}

        <Box
          className="flex flex-col gap-3"
          data-testid="task-activity-section"
        >
          <Typography variant="h6">Activity</Typography>
          {activityItems.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No activity yet.
            </Typography>
          ) : (
            <Box className="flex flex-col gap-3">
              {activityItems.map((item) => (
                <Box
                  key={item.id}
                  className="flex items-center gap-2"
                  sx={{ color: "text.secondary" }}
                >
                  <UserAvatar
                    userId={item.actor.userId}
                    name={item.actor.label}
                    size={21}
                    fontSize={9}
                    initialsOverride={item.actor.initials}
                  />
                  <Typography variant="body2" color="text.primary">
                    {item.message}
                  </Typography>
                  <Tooltip
                    title={moment(item.occurredAt).format("YYYY-MM-DD HH:mm")}
                  >
                    <Typography variant="caption" color="text.secondary">
                      · {moment(item.occurredAt).fromNow()}
                    </Typography>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Delete task</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete this task? This action cannot be
            undone.
          </Typography>
          {deletableAttachmentDocuments.length > 0 && (
            <Typography variant="body2" sx={{ mt: 2 }}>
              <strong>{deletableAttachmentDocuments.length}</strong> attached{" "}
              {deletableAttachmentDocuments.length === 1
                ? "document is"
                : "documents are"}{" "}
              eligible for deletion.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            variant="outlined"
            color="inherit"
          >
            Cancel
          </Button>
          {deletableAttachmentDocuments.length > 0 ? (
            <>
              <Button
                onClick={() => {
                  void handleDeleteConfirm(false);
                }}
                variant="outlined"
                color="error"
              >
                Delete task only
              </Button>
              <Button
                onClick={() => {
                  void handleDeleteConfirm(true);
                }}
                color="error"
                variant="contained"
              >
                Delete task and documents
              </Button>
            </>
          ) : (
            <Button
              onClick={() => {
                void handleDeleteConfirm(false);
              }}
              color="error"
              variant="contained"
            >
              Delete task
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        open={deleteDocumentConfirmOpen}
        onAbort={() => {
          setDeleteDocumentConfirmOpen(false);
          setDocumentToDelete(null);
        }}
        onConfirm={handleDeleteDocumentConfirm}
        title="Delete document"
        content="This will move the attached document to Trash, where it can be restored for 30 days."
      />

      <Dialog
        open={createDocumentModalOpen}
        onClose={() => setCreateDocumentModalOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Create document</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Document title"
            fullWidth
            value={linkedDocumentTitle}
            onChange={(event) => setLinkedDocumentTitle(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => setCreateDocumentModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateLinkedDocument}
            disabled={!linkedDocumentTitle.trim() || isCreatingLinkedDocument}
          >
            Create document
          </Button>
        </DialogActions>
      </Dialog>

      {isTrainingTask && user && task && (
        <TrainingDocumentModal
          isOpen={trainingModalOpen}
          onClose={() => setTrainingModalOpen(false)}
          task={task}
          user={user}
          orgId={orgId}
          documentDeviceId={documentDeviceId}
        />
      )}
    </div>
  );
};

function TaskDetailSkeleton() {
  return (
    <div className="flex flex-1 h-full flex-col p-0 gap-y-4">
      <div className="flex items-center gap-2">
        <Skeleton variant="circular" width={40} height={40} />
        <Skeleton variant="text" width={200} height={40} />
      </div>
      <div className="flex flex-col max-w-[800px] mx-auto w-full mt-2">
        <Skeleton variant="rectangular" height={80} />
        <Skeleton variant="rectangular" height={56} />
        <Skeleton variant="rectangular" height={56} />
        <Skeleton variant="rectangular" height={56} />
        <Skeleton variant="rectangular" height={200} />
      </div>
    </div>
  );
}

export default TaskDetail;
